import KeyboardArrowDown from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUp from "@material-ui/icons/KeyboardArrowUp";
import { useUser } from "@saleor/auth";
import { Zone } from "@saleor/auth/types";
import {
  MaterialPricingGroupEnum,
  useDropdownListMaterialGroup1Query,
  useExportSoldTosLazyQuery,
  useFilterRequireAttentionMaterialLazyQuery,
  useFilterRequireAttentionSaleEmployeeLazyQuery,
  useFilterRequireAttentionSalesGroupQuery,
  useFilterSoldToDomesticOrderLazyQuery,
  useListOrderTypesQuery,
  useListPlantsByBuLazyQuery,
  useListRequireAttentionFlagQuery,
  useListSourceOfAppQuery,
  useScgpMeQuery,
  useSuggestionSearchCreateBySaleOrderLazyQuery
} from "@saleor/graphql";
import {
  FIELD_TYPE,
  StatusSelectionType
} from "@saleor/listOfSalesOrder/components/OrderFilterSection/constant";
import { listOfSaleOrderTrackingUrl } from "@saleor/listOfSalesOrder/urls";
import { useFormik } from "formik";
import uniqBy from "lodash/uniqBy";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import useAppState from "./useAppState";
import { getOrderTypes } from "./utils";

export interface Props {
  handleChangeFilter: (filterObj: any) => void;
  shouldSetDefaultCreatedBy?: boolean;
}

const useOrderFilterSalesOrderList = ({
  handleChangeFilter,
  shouldSetDefaultCreatedBy
}: Props) => {
  const { zone, user } = useUser();
  const [{ filterListSalesOrder }, dispatchAppState] = useAppState();
  const isSaleOrderTracking = location.pathname?.includes(
    listOfSaleOrderTrackingUrl
  );
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      soldTo: filterListSalesOrder?.soldTo || [],
      materialNoMaterialDescription:
        filterListSalesOrder?.materialNoMaterialDescription || [],
      createDate: {
        gte: filterListSalesOrder?.createDate?.gte || null,
        lte: filterListSalesOrder?.createDate?.lte || null
      },
      requestDeliveryDate: {
        gte: filterListSalesOrder?.requestDeliveryDate?.gte || null,
        lte: filterListSalesOrder?.requestDeliveryDate?.lte || null
      },
      bu: filterListSalesOrder?.bu || "",
      saleOrg: filterListSalesOrder?.saleOrg || "",
      channel: filterListSalesOrder?.channel
        ? filterListSalesOrder?.channel
        : zone === Zone.Domestic
        ? "All"
        : "30",
      saleGroup: filterListSalesOrder?.saleGroup || "All",
      orderType: filterListSalesOrder?.orderType || "",
      materialGroup1: filterListSalesOrder?.materialGroup1,
      materialPricingGroup: filterListSalesOrder?.materialPricingGroup || "",
      purchaseOrderNo: filterListSalesOrder?.purchaseOrderNo || "",
      saleOrderNo: filterListSalesOrder?.saleOrderNo || "",
      plant: isSaleOrderTracking
        ? filterListSalesOrder?.plant || "All"
        : filterListSalesOrder?.plant || "",
      deliveryBlock: filterListSalesOrder?.deliveryBlock || "All",
      status: filterListSalesOrder?.status || StatusSelectionType.ALL,
      requireAttentionFlag: filterListSalesOrder?.requireAttentionFlag || "",
      salesEmployeeNo: filterListSalesOrder?.salesEmployeeNo || "",
      createBy: filterListSalesOrder?.createBy || "",
      sourceOfApp: filterListSalesOrder?.sourceOfApp || "All",
      defaultInputSearchCreateBy: shouldSetDefaultCreatedBy
        ? `${user?.firstName || ""} - ${user?.lastName || ""}`
        : ""
    },
    onSubmit: data => {
      const submitData = { ...data };
      delete submitData.defaultInputSearchCreateBy;
      if (!submitData.createDate.gte || !submitData.createDate.lte) {
        delete submitData.createDate;
      }
      if (
        !submitData.requestDeliveryDate.gte ||
        !submitData.requestDeliveryDate.lte
      ) {
        delete submitData.requestDeliveryDate;
      }
      if (submitData?.plant === "All") {
        submitData.plant = "";
      }

      for (const [key, value] of Object.entries(submitData)) {
        if (!value) {
          delete submitData[key];
        }
      }

      handleChangeFilter(submitData);
    }
  });

  const intl = useIntl();
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [isClean, setIsClean] = useState<boolean>(false);

  const { data: orderTypesList } = useListOrderTypesQuery();
  const orderTypeList = useMemo(() => {
    const typesList = getOrderTypes(
      orderTypesList?.listOrderTypes,
      formik.values.bu
    ).map(type => ({
      ...type,
      label: intl.formatMessage(
        { defaultMessage: "{label}" },
        { label: type.label }
      )
    }));
    formik.setFieldValue(
      "orderType",
      typesList.length === 1
        ? typesList[0]?.value
        : typesList.length < 1
        ? ""
        : "All"
    );
    return typesList;
  }, [formik?.values?.bu, orderTypesList?.listOrderTypes]);

  const { data: scgpMe } = useScgpMeQuery({
    displayLoader: true
  });

  const buList = useMemo(
    () =>
      scgpMe?.scgpMe?.extendData?.scgpBus?.map(bu => ({
        label: bu.name,
        value: bu.name
      })),
    [scgpMe?.scgpMe?.extendData?.scgpBus]
  );

  const saleOrgList = useMemo(() => {
    const orgList =
      scgpMe?.scgpMe?.extendData?.salesOrgByBu
        ?.find(org => org?.businessUnit === formik?.values?.bu)
        ?.salesOrganizations?.map(e => ({
          label: `${e.code} - ${e.shortName ? e.shortName : ""}`,
          value: e.code
        })) || [];

    return orgList;
  }, [scgpMe?.scgpMe?.extendData?.salesOrgByBu, formik?.values?.bu]);

  const { data: saleGroup } = useFilterRequireAttentionSalesGroupQuery();
  const { data: requireAttentionFlags } = useListRequireAttentionFlagQuery();
  const { data: productGroup } = useDropdownListMaterialGroup1Query({
    variables: { bu: "PP" }
  });
  const { data: sourceOfApp } = useListSourceOfAppQuery();
  const [
    getSuggestionDomesticSoldTos,
    {
      data: fetchSuggestionDomesticSoldTos,
      loading: loadingSuggestionDomesticSoldTos
    }
  ] = useFilterSoldToDomesticOrderLazyQuery();

  const [
    getExportSoldTos,
    { data: fetchExportSoldTos, loading: loadingExportSoldTos }
  ] = useExportSoldTosLazyQuery();

  const [
    getRequireAttentionSaleEmployee,
    {
      data: fetchRequireAttentionSaleEmployee,
      loading: loadingRequireAttentionSaleEmployee
    }
  ] = useFilterRequireAttentionSaleEmployeeLazyQuery();
  const [
    getRequireAttentionMaterial,
    {
      data: fetchRequireAttentionMaterial,
      loading: loadingRequireAttentionMaterial
    }
  ] = useFilterRequireAttentionMaterialLazyQuery();
  const [
    getCreateBySaleOrder,
    { data: fetchCreateBySaleOrder, loading: loadingCreateBySaleOrder }
  ] = useSuggestionSearchCreateBySaleOrderLazyQuery();

  const [
    getListPlantsByBu,
    { data: fetchListPlantsByBu }
  ] = useListPlantsByBuLazyQuery();

  const ArrowIconComponent = useMemo(
    () => (isOpen ? KeyboardArrowUp : KeyboardArrowDown),
    [isOpen]
  );

  const handleCleanForm = useCallback(() => {
    setIsClean(true);
    formik.resetForm();
    dispatchAppState({
      type: "setFilterListSalesOrder",
      payload: {
        filterListSalesOrder: null
      }
    });
    setTimeout(() => setIsClean(false), 500);
  }, []);

  const channelData = useMemo(() => {
    if (zone === Zone.Domestic) {
      return [
        {
          label: intl.formatMessage({ defaultMessage: "10 - Domestic Direct" }),
          value: "10"
        },
        {
          label: intl.formatMessage({ defaultMessage: "20 - Domestic Agent" }),
          value: "20"
        },
        {
          label: intl.formatMessage({ defaultMessage: "ALL" }),
          value: "All"
        }
      ];
    }
    return [
      {
        label: intl.formatMessage({ defaultMessage: "30 - Export Direct" }),
        value: "30"
      }
    ];
  }, [zone]);

  const getProductGroup = () => {
    formik.setFieldValue("materialGroup1", "");
    if (formik.values?.bu === "CIP") {
      return [];
    }
    const list =
      productGroup?.dropdownListMaterialGroup1.map(e => ({
        label: `${e.materialGroup1}: ${e.materialGroup1Desc}`,
        value: e.materialGroup1
      })) || [];
    if (list.length > 0) {
      list.unshift({
        label: "ALL",
        value: "All"
      });
      formik.setFieldValue("materialGroup1", "All");
    }
    return list;
  };

  const sourceOfAppList = useMemo(() => {
    formik.setFieldValue("sourceOfApp", "");
    const result =
      sourceOfApp?.listSourceOfApp.map(({ value }) => ({
        label: value,
        value
      })) || [];
    if (result.length > 0) {
      if (!result.some(res => res.value === "All")) {
        result.unshift({
          label: "All",
          value: "All"
        });
      }
      formik.setFieldValue("sourceOfApp", "All");
    }
    return result;
  }, [sourceOfApp?.listSourceOfApp]);
  const requireAttentionList = useMemo(() => {
    formik.setFieldValue("requireAttentionFlag", "");
    if (formik.values.bu === "CIP") {
      return [];
    }
    const result =
      requireAttentionFlags?.listRequireAttentionFlag.map(({ key, value }) => ({
        label: key === "ALL" ? "ALL" : value,
        value: key
      })) || [];
    if (result.length > 0 && result.find(e => e.value === "ALL")) {
      formik.setFieldValue("requireAttentionFlag", "ALL");
    }
    return result;
  }, [formik.values.bu, requireAttentionFlags?.listRequireAttentionFlag]);

  const getSaleOrg = (
    salesOrgByBuAndUser: Array<{
      code: string | null;
      name: string | null;
    } | null>
  ) => {
    if (salesOrgByBuAndUser?.length === 1) {
      return salesOrgByBuAndUser[0].code;
    }
    return "";
  };

  useEffect(() => {
    const bu =
      formik?.values?.bu ||
      scgpMe?.scgpMe?.extendData?.defaultBusinessUnit?.name;
    const salesOrgByBuAndUser =
      scgpMe?.scgpMe?.extendData?.salesOrgByBuAndUser?.find(
        org => org?.businessUnit === bu
      )?.salesOrganizations || [];
    formik.setValues(prev => ({
      ...prev,
      bu,
      saleOrg: getSaleOrg(salesOrgByBuAndUser)
    }));
  }, [
    scgpMe?.scgpMe?.extendData?.defaultBusinessUnit?.name,
    formik?.values?.bu
  ]);

  const saleGroupList = useMemo(() => {
    const options = (saleGroup?.filterRequireAttentionSalesGroup || []).map(
      item => ({
        label: `${item.code} - ${item.name}`,
        value: item.code
      })
    );
    options.unshift({ label: "ALL", value: "All" });
    return uniqBy(options, "value");
  }, [saleGroup]);

  useEffect(() => {
    const bu =
      formik.values.bu || scgpMe?.scgpMe?.extendData?.defaultBusinessUnit?.name;
    if (bu && isSaleOrderTracking) {
      getListPlantsByBu({ variables: { bu } });
      formik.setFieldValue("plant", "All");
    }
  }, [formik.values.bu, scgpMe?.scgpMe?.extendData?.defaultBusinessUnit?.name]);

  const plantList = useMemo(
    () =>
      fetchListPlantsByBu?.listPlantsByBu.map(({ plantCode, plantName }) => ({
        label: `${plantCode} - ${plantName}`,
        value: plantCode
      })) || [],
    [
      formik.values.bu,
      scgpMe?.scgpMe?.extendData?.defaultBusinessUnit?.name,
      fetchListPlantsByBu?.listPlantsByBu
    ]
  );

  const formData = useMemo(
    () => [
      {
        title: intl.formatMessage({
          defaultMessage: "Sold to"
        }),
        name: "soldTo",
        placeholder: intl.formatMessage({
          defaultMessage: "ค้นหา Sold to"
        }),
        headerText: "Sold-to ที่เลือกค้นหา",
        keyFilterApi: "search",
        message: "ยังไม่มีข้อมูลที่เลือกค้นหา",
        type: FIELD_TYPE.MULTIPLE_SUGGESTION_INPUT,
        maxLength: 35,
        maxCount: 10,
        md: 6,
        isShowSelected: true
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Material Code / Description"
        }),
        subTitle: "(เลือกค้นหาได้ไม่เกิน 1 รายการ)",
        name: "materialNoMaterialDescription",
        placeholder: intl.formatMessage({
          defaultMessage: "ค้นหา Material Code / Description"
        }),
        type: FIELD_TYPE.MULTIPLE_SUGGESTION_INPUT,
        message: "ยังไม่มีข้อมูลที่เลือกค้นหา",
        maxLength: 35,
        maxCount: 1,
        headerText: "Material Code / Description ที่เลือกค้นหา",
        keyFilterApi: "material",
        md: 6,
        isShowSelected: false
      },
      {
        title: intl.formatMessage({ defaultMessage: "Create Date" }),
        name: "createDate",
        required:
          !formik.values.saleOrderNo &&
          !formik.values.purchaseOrderNo &&
          !(
            formik.values.requestDeliveryDate.gte &&
            formik.values.requestDeliveryDate.lte
          ),
        type: FIELD_TYPE.DATEPICKER,
        maxLength: 35,
        md: 6,
        sm: 12
      },
      {
        title: intl.formatMessage({ defaultMessage: "Request Date" }),
        name: "requestDeliveryDate",
        type: FIELD_TYPE.DATEPICKER,
        maxLength: 35,
        md: 6,
        sm: 12
      },
      {
        title: intl.formatMessage({ defaultMessage: "BU" }),
        name: "bu",
        placeholder: intl.formatMessage({
          defaultMessage: "เลือก BU"
        }),
        type: FIELD_TYPE.SINGLE_SELECT,
        options: buList || []
      },
      {
        title: intl.formatMessage({ defaultMessage: "Sales Organization" }),
        name: "saleOrg",
        placeholder: intl.formatMessage({
          defaultMessage: "เลือก Sales Org."
        }),
        type: FIELD_TYPE.SINGLE_SELECT,
        required: !formik.values.saleOrderNo && !formik.values.purchaseOrderNo,
        options: saleOrgList
      },
      {
        title: intl.formatMessage({ defaultMessage: "Distribution Channel" }),
        name: "channel",
        type: FIELD_TYPE.SINGLE_SELECT,
        options: channelData || [],
        maxLength: 35
      },
      {
        title: intl.formatMessage({ defaultMessage: "Sales Group" }),
        name: "saleGroup",
        type: FIELD_TYPE.SINGLE_SELECT,
        options: saleGroupList
      },
      {
        title: intl.formatMessage({ defaultMessage: "Order Type" }),
        name: "orderType",
        type: FIELD_TYPE.SINGLE_SELECT,
        options: orderTypeList,
        maxLength: 10
      },
      {
        title: intl.formatMessage({ defaultMessage: "Product Group" }),
        name: "materialGroup1",
        placeholder: intl.formatMessage({
          defaultMessage: "เลือก Product Group"
        }),
        type: FIELD_TYPE.SINGLE_SELECT,
        options: getProductGroup(),
        disabled: formik.values.bu === "CIP"
      },
      {
        title: intl.formatMessage({ defaultMessage: "Material Pricing Group" }),
        name: "materialPricingGroup",
        placeholder: intl.formatMessage({
          defaultMessage: "เลือก Pricing Group"
        }),
        type: FIELD_TYPE.SINGLE_SELECT,
        options: [
          {
            label: intl.formatMessage({
              defaultMessage: "Standard"
            }),
            value: MaterialPricingGroupEnum.STANDARD
          },
          {
            label: intl.formatMessage({
              defaultMessage: "Non standard"
            }),
            value: MaterialPricingGroupEnum.NON_STANDARD
          }
        ]
      },
      {
        title: intl.formatMessage({
          defaultMessage: "PO No."
        }),
        name: "purchaseOrderNo",
        placeholder: intl.formatMessage({
          defaultMessage: "ระบุ PO No."
        }),
        type: FIELD_TYPE.SINGLE_INPUT,
        maxLength: 35
      },
      {
        title: intl.formatMessage({
          defaultMessage: "S/O No."
        }),
        name: "saleOrderNo",
        placeholder: intl.formatMessage({
          defaultMessage: "ระบุ S/O No."
        }),
        type: FIELD_TYPE.SINGLE_INPUT,
        maxLength: 10
      },
      isSaleOrderTracking
        ? {
            title: intl.formatMessage({
              defaultMessage: "Plant"
            }),
            name: "plant",
            placeholder: intl.formatMessage({
              defaultMessage: "ระบุ Plant"
            }),
            type: FIELD_TYPE.SINGLE_SELECT,
            options: [{ label: "ALL", value: "All" }, ...plantList]
          }
        : {
            title: intl.formatMessage({
              defaultMessage: "Plant"
            }),
            name: "plant",
            placeholder: intl.formatMessage({
              defaultMessage: "ระบุ Plant"
            }),
            type: FIELD_TYPE.SINGLE_INPUT,
            maxLength: 4
          },
      {
        title: intl.formatMessage({ defaultMessage: "Delivery Blocked" }),
        name: "deliveryBlock",
        placeholder: intl.formatMessage({
          defaultMessage: "เลือก Delivery Blocked"
        }),
        type: FIELD_TYPE.SINGLE_SELECT,
        options: [
          {
            label: intl.formatMessage({ defaultMessage: "ALL" }),
            value: "All"
          },
          {
            label: intl.formatMessage({
              defaultMessage: "Blocked"
            }),
            value: "BLOCK"
          },
          {
            label: intl.formatMessage({
              defaultMessage: "Unblocked"
            }),
            value: "UNBLOCK"
          }
        ]
      },

      {
        title: intl.formatMessage({ defaultMessage: "Status (SAP)" }),
        name: "status",
        placeholder: intl.formatMessage({ defaultMessage: "เลือก Status" }),
        type: FIELD_TYPE.SINGLE_SELECT,
        options: [
          {
            label: intl.formatMessage({ defaultMessage: "ALL" }),
            value: StatusSelectionType.ALL
          },
          {
            label: intl.formatMessage({ defaultMessage: "PENDING" }),
            value: StatusSelectionType.PENDING
          },
          {
            label: intl.formatMessage({ defaultMessage: "COMPLETE" }),
            value: StatusSelectionType.COMPLETE
          }
        ]
      },
      {
        title: intl.formatMessage({ defaultMessage: "Attention Type" }),
        name: "requireAttentionFlag",
        placeholder: intl.formatMessage({
          defaultMessage: "เลือก Attention Type"
        }),
        type: FIELD_TYPE.SINGLE_SELECT,
        options: requireAttentionList,
        disabled: formik.values.bu === "CIP"
      },
      {
        title: intl.formatMessage({ defaultMessage: "Sale Employee" }),
        name: "salesEmployeeNo",
        placeholder: intl.formatMessage({
          defaultMessage: "ค้นหา Sale Employee"
        }),
        type: FIELD_TYPE.SUGGESTION_INPUT,
        md: 3
      },
      {
        title: intl.formatMessage({ defaultMessage: "Created By" }),
        name: "createBy",
        placeholder: intl.formatMessage({
          defaultMessage: "ค้นหา Created By"
        }),
        type: FIELD_TYPE.SUGGESTION_INPUT
      },
      {
        title: intl.formatMessage({ defaultMessage: "Source Of App" }),
        name: "sourceOfApp",
        placeholder: intl.formatMessage({
          defaultMessage: "เลือก Source Of App"
        }),
        type: FIELD_TYPE.SINGLE_SELECT,
        options: sourceOfAppList,
        md: 4
      }
    ],
    [
      buList,
      saleOrgList,
      saleGroupList,
      requireAttentionFlags,
      zone,
      productGroup,
      sourceOfAppList,
      plantList,
      formik.values.saleOrderNo,
      formik.values.purchaseOrderNo,
      formik.values.requestDeliveryDate
    ]
  );

  const setDataFormik = data => {
    formik.setValues(data);
  };

  return useMemo(
    () => ({
      formik,
      setDataFormik,
      isOpen,
      setIsOpen,
      isClean,
      setIsClean,
      ArrowIconComponent,
      handleCleanForm,
      formData,
      getSuggestionSoldTos:
        zone === Zone.Domestic
          ? getSuggestionDomesticSoldTos
          : getExportSoldTos,
      fetchSuggestionSoldTos:
        zone === Zone.Domestic
          ? fetchSuggestionDomesticSoldTos
          : fetchExportSoldTos,
      loadingSuggestionSoldTos:
        zone === Zone.Domestic
          ? loadingSuggestionDomesticSoldTos
          : loadingExportSoldTos,
      getRequireAttentionSaleEmployee,
      fetchRequireAttentionSaleEmployee,
      loadingRequireAttentionSaleEmployee,
      getRequireAttentionMaterial,
      fetchRequireAttentionMaterial,
      loadingRequireAttentionMaterial,
      getCreateBySaleOrder,
      fetchCreateBySaleOrder,
      loadingCreateBySaleOrder,
      scgpMe
    }),
    [
      formik,
      isOpen,
      setIsOpen,
      isClean,
      setIsClean,
      ArrowIconComponent,
      handleCleanForm,
      formData,
      getSuggestionDomesticSoldTos,
      fetchSuggestionDomesticSoldTos,
      loadingSuggestionDomesticSoldTos,
      getExportSoldTos,
      fetchExportSoldTos,
      loadingExportSoldTos,
      getRequireAttentionSaleEmployee,
      fetchRequireAttentionSaleEmployee,
      loadingRequireAttentionSaleEmployee,
      getRequireAttentionMaterial,
      fetchRequireAttentionMaterial,
      loadingRequireAttentionMaterial,
      getCreateBySaleOrder,
      fetchCreateBySaleOrder,
      loadingCreateBySaleOrder,
      scgpMe
    ]
  );
};

export default useOrderFilterSalesOrderList;
